'use client'

import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './dialog'
import { Button } from './button'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
}

type Resolver = (ok: boolean) => void

interface InternalState {
  open: boolean
  options: ConfirmOptions
  resolver: Resolver | null
}

const DEFAULT_STATE: InternalState = {
  open: false,
  options: { title: '' },
  resolver: null,
}

const ConfirmContext = React.createContext<(opts: ConfirmOptions) => Promise<boolean>>(
  () => Promise.resolve(false),
)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<InternalState>(DEFAULT_STATE)

  const confirm = React.useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        setState({ open: true, options, resolver: resolve })
      }),
    [],
  )

  const close = React.useCallback((ok: boolean) => {
    setState((prev) => {
      prev.resolver?.(ok)
      return { ...prev, open: false, resolver: null }
    })
  }, [])

  const {
    title,
    description,
    confirmLabel = 'Bevestigen',
    cancelLabel = 'Annuleren',
    variant = 'default',
  } = state.options

  const confirmClasses =
    variant === 'destructive'
      ? 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600'
      : 'bg-brand-lime text-brand-black hover:bg-brand-lime/90 focus-visible:ring-brand-lime'

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(v) => !v && close(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-brand-gray-dark">{title}</DialogTitle>
            {description && (
              <DialogDescription className="text-brand-gray-mid">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => close(false)}
              className="border-gray-300 text-brand-gray-dark"
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              onClick={() => close(true)}
              className={confirmClasses}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  return React.useContext(ConfirmContext)
}
